import { Dialog, DialogTrigger, DialogContent, DialogTitle, DialogHeader } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { FaUpload } from "react-icons/fa"
import { IoCloudUpload } from "react-icons/io5"
import { ResourceType } from "../Interfaces"
import { FilePreview } from "@/components/UploadFile/UI/FilePreview"
import { useResourceUploader } from "../hooks/useResourceUploader"
import { UploadedFilesList } from "./UploadedFilesList"

// Uploader for the resources
export function ResourceUploader({ type, onResourceRefresh }: { type: ResourceType, onResourceRefresh: () => void }) {
    const {
        totalFiles,
        isUploading,
        error,
        uploadSuccess,
        isDialogOpen,
        uploadingCurrentFile,
        uploadedFiles,
        isDragActive,
        handleUpload,
        onRemove,
        onClearFiles,
        handleDialogChange,
        getRootProps,
        getInputProps,
        MAX_FILE_SIZE,
        SUPPORTED_FILE_EXTENSIONS
    } = useResourceUploader({ type, onResourceRefresh })

    return (
        <UploadDialog 
            isOpen={isDialogOpen}
            onOpenChange={handleDialogChange}
            type={type}
            error={error}
            uploadSuccess={uploadSuccess}
            isUploading={isUploading}
            uploadingCurrentFile={uploadingCurrentFile}
            isDragActive={isDragActive}
            totalFiles={totalFiles}
            onUpload={handleUpload}
            onRemove={onRemove}
            onClearFiles={onClearFiles}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            supportedFormats={SUPPORTED_FILE_EXTENSIONS}
            maxFileSize={MAX_FILE_SIZE}
            uploadedFiles={uploadedFiles}
            resourceType={type}
            onResourceRefresh={onResourceRefresh}
        />
    )
}


// Main upload dialog component
function UploadDialog({
    isOpen,
    onOpenChange,
    type,
    error,
    uploadSuccess,
    isUploading,
    uploadingCurrentFile,
    isDragActive,
    totalFiles,
    onUpload,
    onRemove,
    onClearFiles,
    getRootProps,
    getInputProps,
    supportedFormats,
    maxFileSize,
    uploadedFiles,
    resourceType,
    onResourceRefresh
}: {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    type: ResourceType;
    error: string | null;
    uploadSuccess: boolean;
    isUploading: boolean;
    uploadingCurrentFile: File | null;
    isDragActive: boolean;
    totalFiles: File[];
    onUpload: () => void;
    onRemove: (file: File) => void;
    onClearFiles: () => void;
    getRootProps: () => any;
    getInputProps: () => any;
    supportedFormats: string[];
    maxFileSize: number;
    uploadedFiles: Array<{ name: string; type: string; size: number }>;
    resourceType: ResourceType;
    onResourceRefresh: () => void;
}) {
    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <UploadTriggerButton />
            <UploadDialogContent 
                type={type}
                error={error}
                uploadSuccess={uploadSuccess}
                isUploading={isUploading}
                uploadingCurrentFile={uploadingCurrentFile}
                isDragActive={isDragActive}
                totalFiles={totalFiles}
                onUpload={onUpload}
                onRemove={onRemove}
                onClearFiles={onClearFiles}
                getRootProps={getRootProps}
                getInputProps={getInputProps}
                supportedFormats={supportedFormats}
                maxFileSize={maxFileSize}
                uploadedFiles={uploadedFiles}
                resourceType={resourceType}
                onResourceRefresh={onResourceRefresh}
            />
        </Dialog>
    )
}

// Upload trigger button
function UploadTriggerButton() {
    return (
        <DialogTrigger asChild>
            <Button variant="outline" className="bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300">
                <IoCloudUpload className="w-4 h-4 mr-2" />
                Upload Files
            </Button>
        </DialogTrigger>
    )
}

// Dialog content wrapper
function UploadDialogContent({
    type,
    error,
    uploadSuccess,
    isUploading,
    uploadingCurrentFile,
    isDragActive,
    totalFiles,
    onUpload,
    onRemove,
    onClearFiles,
    getRootProps,
    getInputProps,
    supportedFormats,
    maxFileSize,
    uploadedFiles,
    resourceType,
    onResourceRefresh
}: {
    type: ResourceType;
    error: string | null;
    uploadSuccess: boolean;
    isUploading: boolean;
    uploadingCurrentFile: File | null;
    isDragActive: boolean;
    totalFiles: File[];
    onUpload: () => void;
    onRemove: (file: File) => void;
    onClearFiles: () => void;
    getRootProps: () => any;
    getInputProps: () => any;
    supportedFormats: string[];
    maxFileSize: number;
    uploadedFiles: Array<{ name: string; type: string; size: number }>;
    resourceType: ResourceType;
    onResourceRefresh: () => void;
}) {
    return (
        <DialogContent className="max-w-2xl">
            <UploadDialogHeader type={type} />
            <UploadDialogBody 
                error={error}
                uploadSuccess={uploadSuccess}
                isUploading={isUploading}
                uploadingCurrentFile={uploadingCurrentFile}
                isDragActive={isDragActive}
                totalFiles={totalFiles}
                onUpload={onUpload}
                onRemove={onRemove}
                onClearFiles={onClearFiles}
                getRootProps={getRootProps}
                getInputProps={getInputProps}
                supportedFormats={supportedFormats}
                maxFileSize={maxFileSize}
                uploadedFiles={uploadedFiles}
                resourceType={resourceType}
                onResourceRefresh={onResourceRefresh}
            />
        </DialogContent>
    )
}

// Dialog header with title and icon
function UploadDialogHeader({ type }: { type: ResourceType }) {
    return (
        <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
                <IoCloudUpload className="w-5 h-5 text-orange-500" />
                Upload {type.charAt(0).toUpperCase() + type.slice(1)} Resources
            </DialogTitle>
        </DialogHeader>
    )
}

// Dialog body with all content
function UploadDialogBody({
    error,
    uploadSuccess,
    isUploading,
    uploadingCurrentFile,
    isDragActive,
    totalFiles,
    onUpload,
    onRemove,
    onClearFiles,
    getRootProps,
    getInputProps,
    supportedFormats,
    maxFileSize,
    uploadedFiles,
    resourceType,
    onResourceRefresh
}: {
    error: string | null;
    uploadSuccess: boolean;
    isUploading: boolean;
    uploadingCurrentFile: File | null;
    isDragActive: boolean;
    totalFiles: File[];
    onUpload: () => void;
    onRemove: (file: File) => void;
    onClearFiles: () => void;
    getRootProps: () => any;
    getInputProps: () => any;
    supportedFormats: string[];
    maxFileSize: number;
    uploadedFiles: Array<{ name: string; type: string; size: number }>;
    resourceType: ResourceType;
    onResourceRefresh: () => void;
}) {
    return (
        <div className="mt-4">
            <NotificationMessages error={error} uploadSuccess={uploadSuccess} />
            <DropZoneArea 
                isUploading={isUploading}
                uploadingCurrentFile={uploadingCurrentFile}
                isDragActive={isDragActive}
                totalFiles={totalFiles}
                onRemove={onRemove}
                getRootProps={getRootProps}
                getInputProps={getInputProps}
                maxFileSize={maxFileSize}
            />
            <ActionButtons 
                totalFiles={totalFiles}
                isUploading={isUploading}
                uploadSuccess={uploadSuccess}
                onUpload={onUpload}
                onClearFiles={onClearFiles}
            />
            <SupportedFormatsInfo 
                supportedFormats={supportedFormats}
                maxFileSize={maxFileSize}
            />
            <UploadedFilesList 
                uploadedFiles={uploadedFiles}
                resourceType={resourceType}
                onDescriptionUpdated={onResourceRefresh}
            />
        </div>
    )
}

// Error and success notification messages
function NotificationMessages({ error, uploadSuccess }: { error: string | null; uploadSuccess: boolean }) {
    return (
        <>
            {error && <ErrorMessage message={error} />}
            {uploadSuccess && <SuccessMessage />}
        </>
    )
}

// Error message component
function ErrorMessage({ message }: { message: string }) {
    return (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {message}
        </div>
    )
}

// Success message component
function SuccessMessage() {
    return (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            Files uploaded successfully! 🎉
        </div>
    )
}

// Drop zone area with dynamic styling
function DropZoneArea({
    isUploading,
    uploadingCurrentFile,
    isDragActive,
    totalFiles,
    onRemove,
    getRootProps,
    getInputProps,
    maxFileSize,
}: {
    isUploading: boolean;
    uploadingCurrentFile: File | null;
    isDragActive: boolean;
    totalFiles: File[];
    onRemove: (file: File) => void;
    getRootProps: () => any;
    getInputProps: () => any;
    maxFileSize: number;
}) {
    return (
        <DropZoneContainer 
            isDragActive={isDragActive}
            isUploading={isUploading}
            getRootProps={getRootProps}
        >
            <DropZoneContent 
                isUploading={isUploading}
                isDragActive={isDragActive}
                totalFiles={totalFiles}
                onRemove={onRemove}
                getInputProps={getInputProps}
                maxFileSize={maxFileSize}
                uploadingCurrentFile={uploadingCurrentFile}
            />
        </DropZoneContainer>
    )
}

// Drop zone container with dynamic styling
function DropZoneContainer({
    isDragActive,
    isUploading,
    getRootProps,
    children
}: {
    isDragActive: boolean;
    isUploading: boolean;
    getRootProps: () => any;
    children: React.ReactNode;
}) {
    return (
        <div 
            className={`
                flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-xl 
                transition-colors duration-200 min-h-[200px]
                ${isDragActive ? 'border-orange-400 bg-orange-50' : 'border-gray-300 bg-gray-50'}
                ${!isUploading ? 'cursor-pointer hover:border-orange-300 hover:bg-orange-25' : 'cursor-not-allowed'}
            `}
            {...getRootProps()}
        >
            {children}
        </div>
    )
}

// Drop zone content (loading state or upload screen)
function DropZoneContent({
    isUploading,
    isDragActive,
    totalFiles,
    onRemove,
    getInputProps,
    maxFileSize,
    uploadingCurrentFile
}: {
    isUploading: boolean;
    isDragActive: boolean;
    totalFiles: File[];
    onRemove: (file: File) => void;
    getInputProps: () => any;
    maxFileSize: number;
    uploadingCurrentFile: File | null;
}) {
    if (isUploading) {
        return <UploadingState uploadingCurrentFile={uploadingCurrentFile} />
    }

    return (
        <ResourceUploadScreen 
            files={totalFiles} 
            isDragActive={isDragActive} 
            getInputProps={getInputProps} 
            onRemove={onRemove}
            maxFileSize={maxFileSize}
        />
    )
}

// Uploading state with spinner
function UploadingState({ uploadingCurrentFile }: { uploadingCurrentFile: File | null }) {
    return (
        <div className="text-center">
            <UploadSpinner />
            <UploadingText uploadingCurrentFile={uploadingCurrentFile} />
        </div>
    )
}

// Upload spinner component
function UploadSpinner() {
    return (
        <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
    )
}

// Uploading text component
function UploadingText({ uploadingCurrentFile }: { uploadingCurrentFile: File | null }) {
    return <p className="text-gray-600">Uploading files... (Current file: {uploadingCurrentFile?.name})</p>
}

// Action buttons (upload and clear)
function ActionButtons({
    totalFiles,
    isUploading,
    uploadSuccess,
    onUpload,
    onClearFiles
}: {
    totalFiles: File[]
    isUploading: boolean
    uploadSuccess: boolean
    onUpload: () => void
    onClearFiles: () => void
}) {
    if (totalFiles.length === 0 || isUploading || uploadSuccess) {
        return null
    }

    return (
        <div className="mt-4 flex gap-3">
            <UploadButton 
                fileCount={totalFiles.length}
                onUpload={onUpload}
            />
            <ClearButton onClear={onClearFiles} />
        </div>
    )
}

// Upload button with file count
function UploadButton({ fileCount, onUpload }: { fileCount: number, onUpload: () => void }) {
    return (
        <Button 
            onClick={onUpload}
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
        >
            <FaUpload className="w-4 h-4 mr-2" />
            Upload {fileCount} {fileCount === 1 ? 'file' : 'files'}
        </Button>
    )
}

// Clear files button
function ClearButton({ onClear }: { onClear: () => void }) {
    return (
        <Button 
            variant="outline" 
            onClick={onClear}
            className="px-4"
        >
            Clear
        </Button>
    )
}

// Supported formats information
function SupportedFormatsInfo({ supportedFormats, maxFileSize }: { supportedFormats: string[]; maxFileSize: number }) {
    return (
        <div className="mt-4 text-xs text-gray-500 text-center">
            Supported formats: {supportedFormats.join(', ').toUpperCase()} • Max size: {maxFileSize}MB per file
        </div>
    )
}

function ResourceUploadScreen(props: {
    files: File[];
    isDragActive: boolean;
    getInputProps: () => React.InputHTMLAttributes<HTMLInputElement>;
    onRemove: (file: File) => void;
    maxFileSize: number;
}) {
    const { files, isDragActive, getInputProps, onRemove, maxFileSize } = props
    return (
        <div>
            <IoCloudUpload className="mx-auto text-4xl text-gray-500" />

            {files.length > 0 ? files.map(file => (
                <FilePreview key={file.name} file={file} onRemove={onRemove} />
            )) : <EmptyFilePreview isDragActive={isDragActive} maxFileSize={maxFileSize} />}

            <input {...getInputProps()} />
        </div>
    );
}

function EmptyFilePreview({ isDragActive, maxFileSize }: { isDragActive: boolean; maxFileSize: number }) {
    return (
        <div>
            {isDragActive ?
                <p className="text-gray-500">Drop the files here ...</p>
                :
                <p className="text-gray-500">Drag 'n' drop a files here, or click to select files</p>}

            <p className="text-gray-500">Max size: {maxFileSize}MB</p>
        </div>
    );
}
